import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import type { AfterContentInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  PLATFORM_ID,
  QueryList,
  ViewChild,
} from '@angular/core';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import {
  CalloutColor,
  CalloutComponent,
  CalloutPlacement,
  CalloutViewportFit,
} from '@freelancer/ui/callout';
import { IconModule } from '@freelancer/ui/icon';
import { IconColor, IconName, IconSize } from '@freelancer/ui/icon';
import { FontWeight } from '@freelancer/ui/text';
import type { Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { MoreOptionsItemComponent } from './more-options-item.component';
import {
  MoreOptionsIconColor,
  MoreOptionsIconSize,
  MoreOptionsIconType,
  MoreOptionsItemEvent,
} from './more-options.types';
import { CalloutContentComponent } from '../callout/callout-content.component';
import { CalloutTriggerComponent } from '../callout/callout-trigger.component';
import { DrawerComponent, DrawerPosition } from '../drawer';

@Component({
  standalone: true,
  selector: 'fl-more-options',
  imports: [
    CalloutComponent,
    CalloutContentComponent,
    CalloutTriggerComponent,
    DrawerComponent,
    FreelancerAsyncPipeModule,
    IconModule,
    NgTemplateOutlet,
  ],
  template: `
    <div class="mobile:show">
      <div (click)="drawerBottom?.open()">
        <ng-container *ngTemplateOutlet="icon"></ng-container>
      </div>

      @if (isClientSide) {
        <fl-drawer
          #drawerBottom
          [position]="DrawerPosition.BOTTOM"
          [edgeToEdge]="true"
        >
          <ng-template #drawerTemplate>
            <ng-container *ngTemplateOutlet="content"></ng-container>
          </ng-template>
        </fl-drawer>
      }
    </div>

    <div class="mobile:hide">
      <ng-container *ngTemplateOutlet="desktopContent"></ng-container>
    </div>

    <ng-template #desktopContent>
      <fl-callout
        class="MoreOptions"
        [color]="calloutColor"
        [edgeToEdge]="true"
        [hideCloseButton]="true"
        [hover]="hover"
        [placement]="calloutPlacement"
        [mobileCloseButton]="false"
        [mobileDropdown]="mobileDropdown"
        [viewportFit]="viewportFit"
        (calloutOpen)="toggleState()"
        (calloutClose)="toggleState()"
      >
        <fl-callout-trigger>
          <ng-container *ngTemplateOutlet="icon"></ng-container>
        </fl-callout-trigger>

        <fl-callout-content>
          <ng-container *ngTemplateOutlet="content"></ng-container>
        </fl-callout-content>
      </fl-callout>
    </ng-template>

    <ng-template #content>
      <div
        class="MoreOptions-content"
        [attr.data-color]="calloutColor"
      >
        <ng-content></ng-content>
      </div>
    </ng-template>

    <ng-template #icon>
      <fl-icon
        class="MoreOptions-trigger"
        [class.IsActive]="isActive"
        [class.IsDark]="iconColor === IconColor.FOREGROUND"
        [attr.data-trigger-size]="size"
        [clickable]="true"
        [name]="iconName"
        [label]="moreOptionsLabel"
        [showTitle]="iconShowTitle"
        [size]="size"
        [color]="iconColor"
        [weight]="FontWeight.LIGHT"
        [useIconFont]="useIconFont"
      ></fl-icon>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./more-options.component.scss'],
})
export class MoreOptionsComponent implements AfterContentInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  changeDetectorRef = inject(ChangeDetectorRef);

  DrawerPosition = DrawerPosition;
  IconColor = IconColor;
  FontWeight = FontWeight;

  iconName: IconName = 'ui-show-more-h';
  isActive = false;
  isClientSide = isPlatformBrowser(this.platformId);

  private subscriptions: readonly Subscription[] = [];
  private itemsSubscription?: Subscription;

  @ViewChild('drawerBottom') drawerBottom: DrawerComponent;

  @Input() iconColor: MoreOptionsIconColor = IconColor.FOREGROUND;
  @Input() calloutColor = CalloutColor.LIGHT;
  @Input() calloutPlacement = CalloutPlacement.BOTTOM;
  @Input() disableCalloutClose = false;
  @Input() hover = false;
  @Input() size: MoreOptionsIconSize = IconSize.LARGE;
  @Input() mobileDropdown = false;
  @Input() useIconFont = true;
  @Input() iconShowTitle = true;
  @Input() viewportFit: CalloutViewportFit = { enabled: false };

  moreOptionsLabel = $localize`More Options`;

  @Input() set iconType(type: MoreOptionsIconType) {
    this.iconName =
      type === MoreOptionsIconType.COG
        ? 'ui-cog-v2'
        : type === MoreOptionsIconType.MORE_VERT
          ? 'ui-more-vert'
          : 'ui-show-more-h';
  }

  @Output() calloutToggled = new EventEmitter<boolean>();

  @ViewChild(CalloutComponent)
  calloutComponent: CalloutComponent;

  @ContentChildren(MoreOptionsItemComponent)
  items: QueryList<MoreOptionsItemComponent>;

  // FIXME: T300116 Enabling hydration errors on the webapp server
  // TypeError: Cannot read properties of null (reading 'parentElement')
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  ngAfterContentInit(): void {
    // Keep track of the changes to update subscription list
    this.itemsSubscription = this.items.changes
      .pipe(startWith(this.items.toArray()))
      .subscribe(() => {
        // Clear out all subscriptions to avoid duplicates
        this.clearSubscriptions();

        this.subscriptions = this.items.map((item: MoreOptionsItemComponent) =>
          // FIXME: T267853 -
          // eslint-disable-next-line rxjs/no-nested-subscribe
          item.itemSelected.subscribe((event: MoreOptionsItemEvent) => {
            if (event.closeOnSelect) {
              this.closeCallout('mobile');
            }
            if (event.closeOnSelectDesktop) {
              this.closeCallout('desktop');
            }
          }),
        );
      });
  }

  toggleState(): void {
    this.isActive = !this.isActive;
    this.calloutToggled.emit(this.isActive);
  }

  public closeCallout(mode?: 'desktop' | 'mobile'): void {
    if (
      !this.disableCalloutClose &&
      this.calloutComponent?.calloutIsOpen &&
      mode !== 'mobile'
    ) {
      this.calloutComponent.close();
    }

    if (mode !== 'desktop') {
      this.drawerBottom?.close();
    }
  }

  private clearSubscriptions(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(s => s.unsubscribe());
    }
  }

  ngOnDestroy(): void {
    this.clearSubscriptions();

    if (this.itemsSubscription) {
      this.itemsSubscription.unsubscribe();
    }
  }
}
